import type { VideoModel } from "@ziggapp/klap-core/video";
import type { Database } from "../types/supabase";

export type VideoRow = Database["public"]["Tables"]["videos_v2"]["Row"];

/**
 * What the editor's autosave writes, model field → row column (the set
 * `klap.video.toRowPatch` emits, minus the author flags). Two rows that
 * agree on these columns differ only in metadata — export counter,
 * schedule config, like/viewed flags, status — which backend writers and
 * list pages bump without touching the edit.
 */
export const VIDEO_CONTENT_FIELDS = {
  tracks: "tracks",
  config: "config",
  visualObjects: "visual_objects",
  publicationCaptions: "publication_captions",
  name: "name",
  stylePresetId: "style_preset_id",
  baseTemplateId: "base_template_id",
  width: "width",
  height: "height",
  folderId: "folder_id",
} as const satisfies Partial<Record<keyof VideoModel, keyof VideoRow>>;

const CONTENT_MODEL_FIELDS = Object.keys(VIDEO_CONTENT_FIELDS) as Array<
  keyof typeof VIDEO_CONTENT_FIELDS
>;
const CONTENT_COLUMNS = Object.values(VIDEO_CONTENT_FIELDS);

export const isContentUpdate = (update: Partial<VideoModel>) =>
  CONTENT_MODEL_FIELDS.some((field) => update[field] !== undefined);

export const isMetadataOnlyRowChange = (prev: VideoRow, next: VideoRow) =>
  CONTENT_COLUMNS.every(
    (column) =>
      JSON.stringify(prev[column] ?? null) ===
      JSON.stringify(next[column] ?? null),
  );

export type VideoSaveConflictKind = "changed" | "deleted";

/**
 * Thrown by the video autosave when the row no longer matches the revision
 * this client last saw: another device or tab saved a newer version
 * (`changed`), or the row is gone (`deleted`).
 */
export class VideoSaveConflictError extends Error {
  readonly kind: VideoSaveConflictKind;

  constructor(kind: VideoSaveConflictKind) {
    super(
      kind === "deleted"
        ? "Video was deleted elsewhere"
        : "Video was changed elsewhere",
    );
    this.name = "VideoSaveConflictError";
    this.kind = kind;
  }
}
